import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserRoleService } from 'src/app/Common/UserRoleService/userRole.service';
import { UsersService } from 'src/app/master/users/users.service';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { atLeastOneFieldRequiredValidator } from 'src/app/Shared Utilities/validators/atLeastOneFieldRequiredValidator';
import { UserMasterCreateRequestDTO, UserMasterDTO } from 'src/app/master/users/users.interface';

import { UserRoleCreateRequestDTO } from 'src/app/Common/UserRoleService/userRole.interface';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { UserLoginCreateRequestDTO } from 'src/app/login/userlogin.interface';
import { IdentityValidationService } from 'src/app/Shared Utilities/CustomFunctions/IdentityValidationService';
import { notTodayOrFutureValidator } from 'src/app/Shared Utilities/CustomFunctions/CustomFunctions';

@Component({
  selector: 'app-signup',
  standalone: false,
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css']
})
export class SignupComponent {
  private userMasterService = inject(UsersService);
  private userRoleService = inject(UserRoleService);
  private userLoginService = inject(UserLoginService);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  userNameError: string = ''; // Add for error message
  initialUserName: string = ''; // Add for storing initial username
  isCheckingUserName: boolean = false; // Add for loading spinner
  userNameValid: boolean | null = null; // Add for validation status
  showPassword = false;

  // Unique Identifiers
  private identityValidationService = inject(IdentityValidationService);
  private uniqueIdentifierSubs: Subscription[] = [];

  userMasterForm = new FormGroup({
    userId: new FormControl(0),
    firstName: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z\s]+$/)]),
    middleName: new FormControl('', [Validators.pattern(/^[A-Za-z\s]+$/)]),
    lastName: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-z\s]+$/)]),
    dob: new FormControl('', [Validators.required, notTodayOrFutureValidator]),
    mobileNo: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[6-9]\d{9}$/) // strict 10-digit validation
    ]),
    emailId: new FormControl('', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]),
    designation: new FormControl(''),
    profileImage: new FormControl(''),
    isActive: new FormControl(1),
    gender: new FormControl('', [Validators.required]),
    aadharNumber: new FormControl('', [
      Validators.minLength(12),
      Validators.maxLength(12),
      Validators.pattern(/^[2-9][0-9]{11}$/)]),
    panNumber: new FormControl('', [Validators.minLength(10), Validators.maxLength(10), Validators.pattern(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/)]),
    mappingId: new FormControl(0)
  }, {
    validators: atLeastOneFieldRequiredValidator(['aadharNumber', 'panNumber'])

  });

  userLoginForm = new FormGroup({
    loginId: new FormControl(0),
    userName: new FormControl(''),
    userPassword: new FormControl('')
  });


  ngOnInit(): void {
    this.userLoginForm.controls.userName.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
      if (value) {
        this.validateUserName(value);
      } else {
        this.userNameError = '';
        this.isCheckingUserName = false;
        this.userNameValid = null;
      }
    })

    this.uniqueIdentifierSubs = this.identityValidationService.watchUniqueIdentifiers(this.userMasterForm);
  }


  submitForm() {
    this.userMasterForm.markAllAsTouched();
    this.userLoginForm.markAllAsTouched();

    if (this.userMasterForm.invalid || this.userLoginForm.invalid || this.userNameValid !== true) {

      return;
    }

    const masterData = this.userMasterForm.value;
    const loginData = this.userLoginForm.value;

    let userData: UserMasterCreateRequestDTO = {
      firstName: masterData.firstName!,
      middleName: masterData.middleName!,
      lastName: masterData.lastName!,
      dob: new Date(masterData.dob!),
      gender: masterData.gender ?? '',
      mobileNo: String(masterData.mobileNo!),
      emailId: masterData.emailId!,
      aadharNumber: masterData.aadharNumber?.toString() ?? '',
      panNumber: masterData.panNumber ?? '',
      designation: masterData.designation!,
      profileImage: masterData.profileImage!,
      actionUser: 'self'
    }

    this.userMasterService.UserMaster_Create(userData).subscribe({
      next: (userRes) => {

        let data: UserLoginCreateRequestDTO = {
          userId: userRes.userId,
          userName: loginData.userName!,
          userPassword: loginData.userPassword!,
          actionUser: 'self'
        }

        this.userLoginService.UserLogin_Create(data).subscribe({
          next: (loginRes) => {
            this.mapUserRole(userRes);

            this.toastr.success('Account created successfully! Please log in.', 'Success');

            this.userMasterForm.reset();
            this.userLoginForm.reset();

            this.router.navigate(['/login']);
          }
        })
      }
    })
  }

  mapUserRole(user: UserMasterDTO) {
    let data: UserRoleCreateRequestDTO = {
      userId: user.userId,
      roleId: 4,
      actionUser: 'self'
    }

    this.userRoleService.UserRoleService_Create(data).subscribe({
      next: (res) => {
        console.log(res);
      }
    })
  }

  validateUserName(userName: string) {
    if (userName === this.initialUserName) {
      this.userNameError = '';
      this.isCheckingUserName = false;
      this.userNameValid = true;
      return;
    }

    this.isCheckingUserName = true;
    this.userNameValid = null;

    this.userLoginService.UserLogin_ValidateUserName({
      userName: userName,
    }).subscribe({
      next: (res) => {
        this.isCheckingUserName = false;

        if (res.responseCode === 409) {
          this.userNameError = res.responseMsg || 'Username is already taken';
          this.userNameValid = false;
        } else {
          this.userNameError = '';
          this.userNameValid = true;
        }
      },
      error: (err) => {
        this.isCheckingUserName = false;
        this.userNameError = 'Error Checking Username Availability.';
        this.userNameValid = false;
      }
    })
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}
